import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import styles from './Home.module.css';
import RegistrationForm from './RegistrationForm';
import LoginForm from './Login';
import useDocumentTitle from '../../utils/useDocumentTitle';

const SUITS = ['♠', '♥', '♦', '♣'];

const BOT_DIFFICULTIES = [
    { value: 'easy', label: 'Easy', description: 'Bots throw at random' },
    { value: 'medium', label: 'Medium', description: 'Bots capture points when they can' },
    { value: 'hard', label: 'Hard', description: 'Bots track cards and plan ahead' }
];

function Home() {
    useDocumentTitle(
        'Play ZNK Online — 2v2 Card Game with Friends',
        'Play ZNK free in your browser: a fast 2v2 trick-taking card game. Invite friends with one link, play instantly as a guest, or take on bots offline.'
    );

    const [mode, setMode] = useState('login'); // 'login' | 'register'
    const [showDifficulty, setShowDifficulty] = useState(false);

    return (
        <div className={styles.homeContainer}>
            {/* Animated background particles */}
            <div className={styles.backgroundParticles} />

            {/* Floating card-suit decoration */}
            <div className={styles.suitField} aria-hidden="true">
                {Array.from({ length: 10 }, (_, i) => (
                    <span
                        key={i}
                        className={styles.suit}
                        style={{
                            left: `${(i * 37) % 100}%`,
                            animationDuration: `${18 + (i % 5) * 4}s`,
                            animationDelay: `${-(i * 2.3)}s`,
                            fontSize: `${20 + (i % 4) * 10}px`
                        }}
                    >
                        {SUITS[i % SUITS.length]}
                    </span>
                ))}
            </div>

            <div className={styles.contentWrapper}>
                <motion.div
                    className={styles.hero}
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={styles.brandMark}>
                        <span className={styles.brandIcon}>🎴</span>
                        <span className={styles.brandWord}>ZNK</span>
                    </div>
                    <h1 className={styles.heroTitle}>Play cards with friends, anywhere</h1>
                    <p className={styles.heroSubtitle}>
                        Create a lobby, invite your team, and jump straight into the game.
                    </p>
                </motion.div>

                <motion.div
                    className={styles.authCard}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <div className={styles.tabSwitcher}>
                        <button
                            className={`${styles.tabButton} ${mode === 'login' ? styles.tabButtonActiveLogin : ''}`}
                            onClick={() => setMode('login')}
                        >
                            Sign In
                        </button>
                        <button
                            className={`${styles.tabButton} ${mode === 'register' ? styles.tabButtonActiveRegister : ''}`}
                            onClick={() => setMode('register')}
                        >
                            Create Account
                        </button>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={mode}
                            initial={{ opacity: 0, x: mode === 'login' ? -12 : 12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {mode === 'login' ? <LoginForm /> : <RegistrationForm />}
                        </motion.div>
                    </AnimatePresence>

                    <div className={styles.dividerRow}>
                        <span className={styles.dividerLine} />
                        <span className={styles.dividerText}>or</span>
                        <span className={styles.dividerLine} />
                    </div>

                    <AnimatePresence mode="wait" initial={false}>
                        {!showDifficulty ? (
                            <motion.button
                                key="offline-toggle"
                                type="button"
                                className={styles.offlineButton}
                                onClick={() => setShowDifficulty(true)}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                <span className={styles.offlineButtonIcon}>🤖</span>
                                <span className={styles.offlineButtonText}>
                                    Play offline vs. bots
                                    <span className={styles.offlineButtonSub}>No account, no internet needed</span>
                                </span>
                            </motion.button>
                        ) : (
                            <motion.div
                                key="difficulty-picker"
                                className={styles.difficultyPicker}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                <span className={styles.difficultyPickerLabel}>Choose bot difficulty</span>
                                {BOT_DIFFICULTIES.map(({ value, label, description }) => (
                                    <Link
                                        key={value}
                                        to={`/offline?difficulty=${value}`}
                                        className={styles.difficultyOption}
                                    >
                                        <span className={styles.difficultyOptionLabel}>{label}</span>
                                        <span className={styles.difficultyOptionDescription}>{description}</span>
                                    </Link>
                                ))}
                                <button
                                    type="button"
                                    className={styles.difficultyBack}
                                    onClick={() => setShowDifficulty(false)}
                                >
                                    Back
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </div>
    );
}

export default Home;
